import { centsToDollars } from './formatting';

/**
 * Calculate transportation (delivery) discount based on product subtotal
 * Uses newBooking structure from EditBooking.js
 * Discounts are based on PRODUCT DOLLAR AMOUNT, not distance
 * 
 * @param {Object} params - Parameters for discount calculation
 * @param {Object} params.settings - Settings object containing:
 *   - useDeliveryDiscounts {boolean} - Whether delivery discounts are enabled
 *   - deliveryDiscounts {Array} - Array of delivery discount objects with {id, from, to, value, name}
 * @param {Object} params.newBooking - Booking object with:
 *   - product1 {Array} - Array of product1 items with {id, quantity}
 *   - product2 {Array} - Array of product2 items with {id, quantity, option}
 *   - startDate {Date|Timestamp} - Start date of rental
 *   - endDate {Date|Timestamp} - End date of rental
 *   - delivery {boolean} - Whether delivery is needed
 *   - homePickup {boolean} - Whether home pickup is needed
 * @param {Array} params.products1Catalog - Catalog of product1 items with {id, price (in cents)}
 * @param {Array} params.products2Catalog - Catalog of product2 items with {id, price (in cents), option}
 * @returns {Object} - Discount object with {discountName, discountPercent}
 */
export const calculateTransportationDiscountUtil = ({
  settings,
  newBooking,
  products1Catalog,
  products2Catalog,
}) => {

  // Extract settings values from settings object
  // Settings structure: { useDeliveryDiscounts: { enabled: boolean }, deliveryDiscounts: { discounts: [...] } }
  const useDeliveryDiscounts = settings?.useDeliveryDiscounts?.enabled ?? false;
  const deliveryDiscounts = settings?.deliveryDiscounts?.discounts || [];

  // Extract booking values
  const deliveryNeeded = newBooking?.delivery || false;
  const homePickupNeeded = newBooking?.homePickup || false;

  // Calculate rental days
  let days = 0;
  if (newBooking?.startDate && newBooking?.endDate) {
    // Parse dates (handle Firestore Timestamp, Date, string, or serialized Firestore timestamp)
    const parseDate = (date) => {
      if (date instanceof Date) return date;
      if (date?.toDate) return date.toDate();
      // Handle serialized Firestore timestamp (from JSON.parse(JSON.stringify()))
      if (date?.type === "firestore/timestamp/1.0" && date?.seconds !== undefined) {
        return new Date(date.seconds * 1000 + (date.nanoseconds || 0) / 1000000);
      }
      return new Date(date);
    };
    
    const startDate = parseDate(newBooking.startDate);
    const endDate = parseDate(newBooking.endDate);
    
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      // Round, not ceil: midnight-to-midnight diffs are whole days except for
      // the DST hour, and ceil billed fall-back ranges an extra day.
      days = Math.round((end - start) / (1000 * 60 * 60 * 24)) || 1;
    }
  }

  // Calculate rentalCostGross (equipment cost in dollars) from products
  let rentalCostGross = 0;

  // Calculate product1 costs (prices are in cents, convert to dollars)
  if (newBooking?.product1 && Array.isArray(newBooking.product1)) {
    newBooking.product1.forEach((item) => {
      const catalogProduct = products1Catalog?.find((p) => p.id === item.id);
      if (catalogProduct) {
        const priceDollars = centsToDollars(catalogProduct.price || 0);
        const itemCost = priceDollars * (item.quantity || 0) * days;
        rentalCostGross += itemCost;
      }
    });
  }

  // Calculate product2 costs (prices are in cents, convert to dollars)
  const totalBikes = newBooking?.product1?.reduce((sum, p) => sum + (p.quantity || 0), 0) || 0;
  
  if (newBooking?.product2 && Array.isArray(newBooking.product2)) {
    newBooking.product2.forEach((item) => {
      const catalogProduct = products2Catalog?.find((p) => p.id === item.id);
      if (catalogProduct) {
        const priceDollars = centsToDollars(catalogProduct.price || 0);
        let itemCost = 0;
        if (catalogProduct.option === "per-booking" && item.quantity > 0) {
          itemCost = priceDollars * item.quantity;
        } else if (catalogProduct.option === "per-bike" && item.quantity > 0) {
          itemCost = priceDollars * item.quantity * totalBikes;
        } else if (catalogProduct.option === "user-selection") {
          itemCost = priceDollars * (item.quantity || 0) * days;
        }
        rentalCostGross += itemCost;
      }
    });
  }

  let deliveryDiscountPercent = 0;
  let deliveryDiscountName = '';

  // Apply delivery discount based on PRODUCT DOLLAR AMOUNT (not mileage)
  const shouldCheckDiscounts = useDeliveryDiscounts && deliveryDiscounts && deliveryDiscounts.length > 0 && (deliveryNeeded || homePickupNeeded);

  if (shouldCheckDiscounts) {
    // Find ALL applicable discounts based on product subtotal (equipment cost in dollars)
    // Discount tiers are stored in cents, convert to dollars for comparison
    const applicableDiscounts = deliveryDiscounts.filter(discount => {
      const discountFromDollars = centsToDollars(discount.from || 0);
      const discountToDollars = centsToDollars(discount.to || 0);
      return rentalCostGross >= discountFromDollars && rentalCostGross <= discountToDollars;
    });
    
    // Apply the HIGHEST discount percentage if multiple match
    if (applicableDiscounts.length > 0) {
      const highestDiscount = applicableDiscounts.reduce((max, discount) => {
        return (discount.value > max.value) ? discount : max;
      }, applicableDiscounts[0]);
      
      deliveryDiscountPercent = highestDiscount.value || 0;
      // Construct discount name from from/to if name doesn't exist (convert cents to dollars for display)
      if (highestDiscount.name) {
        deliveryDiscountName = highestDiscount.name;
      } else if (highestDiscount.from !== undefined && highestDiscount.to !== undefined) {
        const fromDollars = centsToDollars(highestDiscount.from);
        const toDollars = centsToDollars(highestDiscount.to);
        deliveryDiscountName = `$${fromDollars}-${toDollars} order: ${deliveryDiscountPercent}% off delivery`;
      } else {
        deliveryDiscountName = '';
      }
    }
  }

  const result = {
    discountName: deliveryDiscountName,
    discountPercent: deliveryDiscountPercent,
  };
  
  return result;
};

/**
 * Calculate duration discount based on rental days and product costs
 * 
 * @param {Object} params - Parameters for discount calculation
 * @param {Object} params.booking - Booking object with:
 *   - product1 {Array} - Array of product1 items with {id, quantity}
 *   - product2 {Array} - Array of product2 items with {id, quantity}
 *   - startDate {Date|Timestamp} - Start date of rental
 *   - endDate {Date|Timestamp} - End date of rental
 * @param {Array} params.durationDiscounts - Array of duration discount objects with {id, fromDays, toDays, value, name}
 * @param {Array} params.products1Catalog - Catalog of product1 items with {id, price (in cents)}
 * @param {Array} params.products2Catalog - Catalog of product2 items with {id, price (in cents), option}
 * @param {boolean} params.useDurationDiscounts - Whether duration discounts are enabled
 * @returns {Object} - Discount object with {discountAmountCents, discountId, discountPercent, discountName}
 */
export const calculateDurationDiscountUtil = ({
  booking,
  durationDiscounts = [],
  products1Catalog = [],
  products2Catalog = [],
  useDurationDiscounts = false,
}) => {
  // Return 0 if duration discounts are disabled
  if (!useDurationDiscounts || !durationDiscounts || durationDiscounts.length === 0) {
    return { 
      discountAmountCents: 0, 
      discountId: null, 
      discountPercent: 0,
      discountName: ''
    };
  }

  // Calculate rental duration in days
  if (!booking?.startDate || !booking?.endDate) {
    return { 
      discountAmountCents: 0, 
      discountId: null, 
      discountPercent: 0,
      discountName: ''
    };
  }

  // Parse dates (handle Firestore Timestamp, Date, string, or serialized Firestore timestamp)
  const parseDate = (date) => {
    if (date instanceof Date) return date;
    if (date?.toDate) return date.toDate();
    // Handle serialized Firestore timestamp (from JSON.parse(JSON.stringify()))
    if (date?.type === "firestore/timestamp/1.0" && date?.seconds !== undefined) {
      return new Date(date.seconds * 1000 + (date.nanoseconds || 0) / 1000000);
    }
    return new Date(date);
  };

  // Clone after parsing: parseDate hands back the caller's own Date instance,
  // and setHours below would otherwise zero the booking's time-of-day in place.
  const startDate = new Date(parseDate(booking.startDate));
  const endDate = new Date(parseDate(booking.endDate));
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);
  // Round, not ceil — see the transportation util's day count.
  const days = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));

  // Find applicable discount based on rental duration (days)
  const applicableDiscount = durationDiscounts.find((discount) => {
    return days >= discount.fromDays && days <= discount.toDays;
  });

  if (!applicableDiscount) {
    return { 
      discountAmountCents: 0, 
      discountId: null, 
      discountPercent: 0,
      discountName: ''
    };
  }

  // Calculate equipment subtotal from booking products (in cents)
  // IMPORTANT: Duration discounts apply ONLY to product1 (bicycles), not product2 add-ons.
  let equipmentSubtotalCents = 0;

  // Calculate product1 costs (subject to duration discount)
  // Note: Prices in products1Catalog are expected to be in CENTS
  if (booking.product1 && Array.isArray(booking.product1)) {
    booking.product1.forEach((item) => {
      const catalogProduct = products1Catalog.find((p) => p.id === item.id);
      if (catalogProduct) {
        const priceCents = catalogProduct.price || 0;
        equipmentSubtotalCents += priceCents * (item.quantity || 0) * days;
      }
    });
  }

  // Calculate discount amount in cents
  const discountPercent = applicableDiscount.value || 0;
  const discountAmountCents = Math.round(equipmentSubtotalCents * (discountPercent / 100));
  const discountId = applicableDiscount.id || null;
  const discountName = applicableDiscount.name || `${applicableDiscount.fromDays}-${applicableDiscount.toDays} day rental: ${discountPercent}% off`;

  return {
    discountAmountCents,
    discountId,
    discountPercent,
    discountName,
  };
};

